import { useAtom } from "@dbeining/react-atom";
import { Field, Form, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import type {
    GeographyCreatePayload,
    GeographyDeletedRestoreAvailableConflict,
    GeographyUpdatePayload,
    IGeographyDetail,
} from "@/customTypings/reference/Geography";
import {
    GeographyCreateConflictError,
    GEOGRAPHY_TYPES,
    formatGeographyTypeLabel,
    isGeographyDeletedRestoreAvailableConflict,
    isGeographyDeletedTypeMismatchConflict,
} from "@/customTypings/reference/Geography";
import { userAtom } from "../../atoms";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { useViewport } from "../../contexts";
import { IRole } from "../../customTypings/user/Role";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { geographiesService } from "../../services/reference/geographies.service";
import {
    appRoutes,
    arePayloadsEqual,
    buildRecordLifecycleConfirmProps,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    formatReferenceSourceDisplay,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    ListPageTitles,
    loadingPageTitle,
    logErrorForDev,
    NO_CHANGES_TOAST_MESSAGE,
    referenceAdminEditPath,
    referenceAdminListPath,
    Role,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";
import {
    buildGeoAdminPagedListActions,
    formatGeoToastLabel,
    getGeoAdminActionSuccessMessage,
    type GeoAdminConfirmKind,
} from "../GeographiesListPage/geoAdminListActions";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatGeoMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatEffectiveFromDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).format("Do MMMM YYYY");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type FormValues = GeographyCreatePayload & { id?: string; isActiveOnCreate?: boolean };

const geoEditValidationSchema = Yup.object({
    id: Yup.string().optional(),
    code: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Code is required.")
        .max(64, "Code must be at most 64 characters."),
    geographyType: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
        .when("id", {
            is: (id: string | undefined) => !id || id === "",
            then: (schema) =>
                schema
                    .required("Type is required.")
                    .oneOf([...GEOGRAPHY_TYPES], "Type must be Region, Sub-Region, or District."),
            otherwise: (schema) => schema.optional(),
        }),
    description: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Description is required.")
        .max(512, "Description must be at most 512 characters."),
    isActiveOnCreate: Yup.boolean().optional(),
});

type ConfirmKind = GeoAdminConfirmKind | null;

const emptyForm: FormValues = {
    code: "",
    geographyType: "REGION",
    description: "",
    isActiveOnCreate: true,
};

function buildUpdatePayload(values: FormValues): GeographyUpdatePayload {
    const t = (s: string) => (typeof s === "string" ? s.trim() : s);
    return { id: values.id!, code: t(values.code), description: t(values.description) };
}

function buildUpdatePayloadFromDetail(detail: IGeographyDetail): GeographyUpdatePayload {
    return { id: detail.id, code: detail.code, description: detail.description };
}

const GeographyEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const isNew = !id || id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaActive, setMetaActive] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [source, setSource] = useState("");
    const [sourceVersion, setSourceVersion] = useState("");
    const [effectiveFromUtc, setEffectiveFromUtc] = useState<string | null>(null);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);

    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [restoreDeletedConflict, setRestoreDeletedConflict] =
        useState<GeographyDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<{
        description: string;
        isActive: boolean;
    } | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<GeographyUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const validationSchema = useMemo(() => geoEditValidationSchema, []);

    const formAutoFocusRef = useFormAutoFocus(!loading, [loading, id]);

    const headerMenuItem = useMemo(
        () => ({ isActive: metaActive, isDeleted: metaDeleted }),
        [metaActive, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () => buildGeoAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const applyGeoDetail = (geo: IGeographyDetail) => {
        setInitialValues({
            id: geo.id,
            code: geo.code,
            geographyType: geo.geographyType,
            description: geo.description,
        });
        setMetaActive(geo.isActive);
        setMetaDeleted(geo.isDeleted);
        setSource(geo.source);
        setSourceVersion(geo.sourceVersion);
        setEffectiveFromUtc(geo.effectiveFromUtc);
        setCreatedAt(geo.createdAtUtc);
        setUpdatedAt(geo.updatedAtUtc);
        setCreatedByDisplayName(geo.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(geo.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(geo.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(geo.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(geo.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(geo.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(geo);
    };

    useEffect(() => {
        if (isNew || !id) {
            setLoading(false);
            setInitialValues(emptyForm);
            setMetaActive(true);
            setMetaDeleted(false);
            setSource("");
            setSourceVersion("");
            setEffectiveFromUtc(null);
            setCreatedByDisplayName(null);
            setLastUpdatedByDisplayName(null);
            setDeletedAuditAtUtc(null);
            setDeletedByDisplayName(null);
            setDisabledAuditAtUtc(null);
            setDisabledByDisplayName(null);
            return;
        }
        setLoading(true);
        setLoadError(null);
        geographiesService
            .getById(id)
            .then(applyGeoDetail)
            .catch((e: unknown) => {
                logErrorForDev("GeographyEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load geography."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.geography,
                initialValues.code || `this ${EntityLabels.geography.toLowerCase()}`
            ),
        [confirmKind, initialValues.code]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind) return;
        const op =
            kind === "disable"
                ? geographiesService.disable(id)
                : kind === "enable"
                  ? geographiesService.enable(id)
                  : kind === "softDelete"
                    ? geographiesService.softDelete(id)
                    : geographiesService.restore(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getGeoAdminActionSuccessMessage(kind, initialValues.code));
            if (kind === "softDelete") {
                navigate(referenceAdminListPath("geography"), { replace: true });
                return;
            }
            return geographiesService.getById(id).then(applyGeoDetail);
        }).catch((e: unknown) => { setConfirmKind(null); showToastError(getUserFacingErrorMessage(e, DEFAULT_ACTION_FAILED_MESSAGE));
        });
    };

    if (!isAdmin) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage geography.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.geography)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to={referenceAdminListPath("geography")}>Back to {ListPageTitles.geography}</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.geography, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: EntityLabels.referenceData, to: appRoutes.codes },
                    { text: ListPageTitles.geography, to: referenceAdminListPath("geography") },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{
                    borderBottom: "2px solid var(--primary)",
                    paddingBottom: "0.5rem",
                }}
            >
                <h1
                    className="mb-0 flex-grow-1 min-w-0"
                    style={{ borderBottom: "none", paddingBottom: 0 }}
                >
                    {editPageTitle(EntityLabels.geography, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="geo-edit-actions-dropdown"
                        ariaLabel="Geography actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatGeoMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaActive}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    if (metaDeleted) return;
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
                        const code = t(values.code);
                        const description = t(values.description);
                        if (isNew) {
                            try {
                                const created = await geographiesService.create({
                                    code,
                                    geographyType: t(String(values.geographyType)).toUpperCase(),
                                    description,
                                    isActive: values.isActiveOnCreate,
                                });
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatGeoToastLabel(code)} created successfully!`);
                                navigate(referenceAdminEditPath("geography", created.id));
                            } catch (err: unknown) {
                                if (err instanceof GeographyCreateConflictError) {
                                    if (isGeographyDeletedRestoreAvailableConflict(err.conflict)) {
                                        setRestoreDeletedConflict(err.conflict);
                                        setPendingRestoreForm({
                                            description,
                                            isActive: values.isActiveOnCreate ?? true,
                                        });
                                        setLiveValidationAfterSubmit(false);
                                        return;
                                    }
                                    if (isGeographyDeletedTypeMismatchConflict(err.conflict)) {
                                        setLiveValidationAfterSubmit(false);
                                        showToastError(err.conflict.message);
                                        return;
                                    }
                                }
                                throw err;
                            }
                        } else if (values.id) {
                            const updatePayload = buildUpdatePayload(values);
                            if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            const result = await geographiesService.update(updatePayload);
                            if (isUnchangedUpdateResponse(result)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "success";
                            savedUpdatePayloadRef.current = updatePayload;
                            showToastSuccess(`${formatGeoToastLabel(code)} updated successfully!`);
                            resetForm({ values });
                            if (leaveAfterSaveRef.current) return;
                            navigate(referenceAdminListPath("geography"));
                        }
                    } catch (err: unknown) {
                        if (
                            err instanceof GeographyCreateConflictError &&
                            (isGeographyDeletedRestoreAvailableConflict(err.conflict) ||
                                isGeographyDeletedTypeMismatchConflict(err.conflict))
                        ) {
                            return;
                        }
                        saveOutcomeRef.current = "failure";
                        showToastError(getUserFacingErrorMessage(err, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    }
                }}
            >
                {({ isSubmitting, values, errors, touched, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: keyof FormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field as keyof typeof errors,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit,
                            touched
                        );
                    const form = (
                        <Form
                            ref={formAutoFocusRef}
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            {metaDeleted && (
                                <Alert variant="warning" className="mb-3">
                                    This area entry has been removed. Restore it before you make any changes.
                                </Alert>
                            )}

                            <Alert variant="info" className="mb-3">
                                {isNew ? (
                                    <>
                                        Choose <strong>Region</strong>, <strong>Sub-Region</strong>, or{" "}
                                        <strong>District</strong> when creating an entry; type cannot be changed later.
                                        Codes must be unique. The same code cannot be used for another entry, regardless of type.
                                    </>
                                ) : (
                                    <>
                                        <strong>Type</strong> is fixed after creation. <strong>Code</strong> must remain
                                        unique across all area types.
                                    </>
                                )}
                            </Alert>

                            <Row className="g-3">
                                {!isNew && (
                                    <>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Source</label>
                                            <div className="form-control-plaintext py-0">
                                                {formatReferenceSourceDisplay(source) || "-"}
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Source version</label>
                                            <div className="form-control-plaintext py-0">{sourceVersion || "-"}</div>
                                        </Col>
                                        <Col md={4}>
                                            <label className="form-label mb-0">Effective from</label>
                                            <div className="form-control-plaintext py-0">
                                                {effectiveFromUtc ? formatEffectiveFromDate(effectiveFromUtc) : "-"}
                                            </div>
                                        </Col>
                                    </>
                                )}
                                <Col md={6}>
                                    <label className="form-label" htmlFor="code">
                                        Code <span className="text-danger" aria-hidden="true">*</span>
                                        <span className="visually-hidden"> (required)</span>
                                    </label>
                                    <Field
                                        id="code"
                                        name="code"
                                        className={`form-control font-monospace${showErr("code") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted}
                                        aria-required
                                        aria-invalid={showErr("code")}
                                    />
                                    {showErr("code") && (
                                        <div className="invalid-feedback d-block">{errors.code}</div>
                                    )}
                                    <div className="form-text">
                                        Official identifier (max 32 characters). Code must be unique, even
                                        across all types (Region, Sub-Region, District).
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <label className="form-label" htmlFor="geographyType">
                                        Type <span className="text-danger" aria-hidden="true">*</span>
                                        <span className="visually-hidden"> (required)</span>
                                    </label>
                                    {isNew ? (
                                        <Field
                                            id="geographyType"
                                            name="geographyType"
                                            as="select"
                                            className={`form-select${showErr("geographyType") ? " is-invalid" : ""}`}
                                            aria-required
                                            aria-invalid={showErr("geographyType")}
                                        >
                                            {GEOGRAPHY_TYPES.map((t) => (
                                                <option key={t} value={t}>
                                                    {formatGeographyTypeLabel(t)}
                                                </option>
                                            ))}
                                        </Field>
                                    ) : (
                                        <div className="form-control-plaintext py-2">
                                            {formatGeographyTypeLabel(values.geographyType) || "-"}
                                        </div>
                                    )}
                                    {showErr("geographyType") && (
                                        <div className="invalid-feedback d-block">{errors.geographyType}</div>
                                    )}
                                    {!isNew && (
                                        <div className="form-text">Geography Type is fixed after creation.</div>
                                    )}
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label" htmlFor="description">
                                        Description <span className="text-danger" aria-hidden="true">*</span>
                                        <span className="visually-hidden"> (required)</span>
                                    </label>
                                    <Field
                                        id="description"
                                        name="description"
                                        as="textarea"
                                        rows={3}
                                        className={`form-control${showErr("description") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted}
                                        aria-required
                                        aria-invalid={showErr("description")}
                                    />
                                    {showErr("description") && (
                                        <div className="invalid-feedback d-block">{errors.description}</div>
                                    )}
                                </Col>
                                {isNew && (
                                    <Col xs={12}>
                                        <div className="form-check">
                                            <Field
                                                id="isActiveOnCreate"
                                                name="isActiveOnCreate"
                                                type="checkbox"
                                                className="form-check-input"
                                            />
                                            <label className="form-check-label" htmlFor="isActiveOnCreate">
                                                Start as active
                                            </label>
                                        </div>
                                    </Col>
                                )}
                            </Row>

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaActive}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={metaDeleted}
                                    readOnlyForm={metaDeleted}
                                    isNewRecord={isNew}
                                    newRecordLabel={EntityLabels.geography}
                                    enableSaveBeforeLeave={!isNew}
                                    leaveAfterSaveRef={leaveAfterSaveRef}
                                    saveOutcomeRef={saveOutcomeRef}
                                    onCancel={() => navigate(referenceAdminListPath("geography"))}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={metaDeleted}
                            isNewRecord={isNew}
                            newRecordLabel={EntityLabels.geography}
                            enableSaveBeforeLeave={!isNew}
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreDeletedConflict !== null && pendingRestoreForm !== null}
                onClose={async (confirmed) => {
                    if (!confirmed || !restoreDeletedConflict || !pendingRestoreForm) {
                        setRestoreDeletedConflict(null);
                        setPendingRestoreForm(null);
                        return;
                    }
                    setRestoringDeleted(true);
                    try {
                        const restored = await geographiesService.restoreDeletedWithCreateValues({
                            id: restoreDeletedConflict.existingId,
                            description: pendingRestoreForm.description,
                            isActive: pendingRestoreForm.isActive,
                        });
                        setRestoreDeletedConflict(null);
                        setPendingRestoreForm(null);
                        showToastSuccess(
                            `${formatGeoToastLabel(restoreDeletedConflict.existingCode)} restored successfully!`
                        );
                        navigate(referenceAdminEditPath("geography", restored.id));
                    } catch (e: unknown) { showToastError(getUserFacingErrorMessage(e, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    } finally {
                        setRestoringDeleted(false);
                    }
                }}
                title="Restore deleted geography?"
                body={
                    restoreDeletedConflict
                        ? `A removed area entry already exists with code "${restoreDeletedConflict.existingCode}" and type ${restoreDeletedConflict.geographyType}. Restore it with the description and Active choice you entered?`
                        : ""
                }
                buttonSuccessVariant="primary"
                buttonSuccessText={restoringDeleted ? "Restoring…" : "Yes"}
                buttonCancelText="No"
            />
        </Container>
    );
};

export { GeographyEditPage };
